import React, { useState } from 'react';
import { supabase } from '../supabaseClient';

function SignUp({ onSignUpComplete }) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [orgName, setOrgName] = useState('');
  const [orgType, setOrgType] = useState('school');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userId, setUserId] = useState(null);

  const handleCreateAccount = async () => {
    setError('');

    if (!name || !email || !password || !confirmPassword) {
      setError('Please fill in all fields.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setLoading(true);

    const { data, error } = await supabase.auth.signUp({ email, password });

    if (error) {
      setError('Error: ' + error.message);
      setLoading(false);
      return;
    }

    const { error: profileError } = await supabase
      .from('profiles')
      .insert([{
        id: data.user.id,
        email,
        role: 'orgadmin',
        organization_id: null,
      }]);

    if (profileError) {
      setError('Error saving profile: ' + profileError.message);
      setLoading(false);
      return;
    }

    setUserId(data.user.id);
    setLoading(false);
    setStep(2);
  };

  const handleCreateOrg = async () => {
    setError('');

    if (!orgName) {
      setError('Please enter your organization name.');
      return;
    }

    setLoading(true);

    const { data: orgData, error: orgError } = await supabase
      .from('organizations')
      .insert([{ name: orgName, type: orgType, plan: 'free' }])
      .select();

    if (orgError) {
      setError('Error creating organization: ' + orgError.message);
      setLoading(false);
      return;
    }

    const { error: profileError } = await supabase
      .from('profiles')
      .update({ organization_id: orgData[0].id })
      .eq('id', userId);

    if (profileError) {
      setError('Error linking organization: ' + profileError.message);
      setLoading(false);
      return;
    }

    setLoading(false);
    onSignUpComplete(orgData[0]);
  };

  const getOrgIcon = (type) => {
    if (type === 'school') return '🏫';
    if (type === 'daycare') return '🧸';
    if (type === 'church') return '⛪';
    if (type === 'event') return '🎉';
    return '🏢';
  };

  return (
    <div>
      <div style={stepIndicatorStyle}>
        <div style={{ ...stepDotStyle, background: '#1a56db' }}>1</div>
        <div style={stepLineStyle} />
        <div style={{ ...stepDotStyle, background: step >= 2 ? '#1a56db' : '#ddd' }}>2</div>
      </div>

      {step === 1 && (
        <div>
          <div className="header">
            <div className="app-logo">🛡️ Safe<span>Roster</span></div>
            <h2>Create Your Account</h2>
            <p>Get started for free — no credit card required</p>
          </div>

          <input className="input" placeholder="Your Full Name" value={name} onChange={(e) => setName(e.target.value)} />
          <input className="input" placeholder="Email Address" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input className="input" placeholder="Password (min 6 characters)" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <input className="input" placeholder="Confirm Password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />

          <button className="btn btn-primary" onClick={handleCreateAccount} disabled={loading}>
            {loading ? 'Creating Account...' : 'Create Account →'}
          </button>

          {error && <div className="error-message">{error}</div>}

          <p style={{ textAlign: 'center', marginTop: '16px', color: '#888', fontSize: '14px' }}>
            Already have an account?{' '}
            <span style={{ color: '#1a56db', cursor: 'pointer', fontWeight: '600' }} onClick={() => onSignUpComplete(null, 'login')}>
              Log in here
            </span>
          </p>
        </div>
      )}

      {step === 2 && (
        <div>
          <div className="header">
            <h2>🏫 Set Up Your Organization</h2>
            <p>Tell us about your organization</p>
          </div>

          <input className="input" placeholder="Organization Name" value={orgName} onChange={(e) => setOrgName(e.target.value)} />

          <p className="section-title" style={{ marginBottom: '12px' }}>Organization Type</p>
          <div style={orgTypeGridStyle}>
            {['school', 'daycare', 'church', 'event'].map((type) => (
              <div
                key={type}
                style={{
                  ...orgTypeCardStyle,
                  borderColor: orgType === type ? '#1a56db' : '#eee',
                  background: orgType === type ? '#f0f4ff' : 'white',
                }}
                onClick={() => setOrgType(type)}
              >
                <div style={{ fontSize: '28px' }}>{getOrgIcon(type)}</div>
                <p style={{ fontSize: '13px', fontWeight: '600', textTransform: 'capitalize', marginTop: '6px' }}>
                  {type === 'church' ? 'Church' : type}
                </p>
              </div>
            ))}
          </div>

          <div style={freePlanBoxStyle}>
            <p style={{ margin: 0, fontSize: '13px', color: '#1a56db', fontWeight: '600' }}>
              🎉 You're starting on the <strong>Free Plan</strong> — 15 daily check-ins, no credit card required!
            </p>
          </div>

          <button className="btn btn-primary" onClick={handleCreateOrg} disabled={loading}>
            {loading ? 'Setting Up...' : 'Launch SafeRoster →'}
          </button>

          {error && <div className="error-message">{error}</div>}
        </div>
      )}
    </div>
  );
}

const stepIndicatorStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '32px',
};

const stepDotStyle = {
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: '700',
  fontSize: '16px',
};

const stepLineStyle = {
  width: '60px',
  height: '3px',
  background: '#eee',
};

const orgTypeGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '12px',
  marginBottom: '20px',
};

const orgTypeCardStyle = {
  border: '2px solid #eee',
  borderRadius: '12px',
  padding: '16px',
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s',
};

const freePlanBoxStyle = {
  background: '#e8f0fe',
  borderRadius: '12px',
  padding: '14px 16px',
  marginBottom: '20px',
  border: '1px solid #c7d9fb',
};

export default SignUp;